"use client";

import React, { useCallback, useState } from "react";
import Icon from "@/components/icons";
import CurrencyInlinePill from "../CurrencyInlinePill";
import useIsMobile from "@/hooks/useIsMobile";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/contexts/UserPreferencesContext/hooks/useCurrency";
import { DEFAULT_CURRENCY } from "@/lib/currency/constants";
import {
  MINI_BANNER_DISMISS_HTML_ID,
  MINI_BANNER_DISMISS_NAME,
  MINI_BANNER_DISMISS_TEXT,
} from "../trackingConstants";
import {
  CURRENCY_BAR_LABEL_CLASS_NAME,
  DEFAULT_CURRENCY_BAR_LABEL,
  DEFAULT_CURRENCY_BAR_MOBILE_LABEL,
} from "./constants";
import type { MultiCurrencyPdpConfig } from "@/lib/currency/types";
import { BadgeIcon } from "@/components/ui/badgeIcon";
import useTranslations from "@/i18n/useTranslations";

export interface CurrencyBarProps {
  price: number;
  originalPrice?: number;
  badgeText?: string;
  label?: string;
  mobileLabel?: string;
  description?: React.ReactNode;
  tooltip?: string;
  location: string;
  className?: string;
  onCurrencyChange?: (code: string) => void;
  multiCurrencyConfig?: MultiCurrencyPdpConfig;
  compactDismiss?: boolean;
  countryAutoDetection?: boolean;
  wideLabelGap?: boolean;
}

export default function CurrencyBar({
  price,
  originalPrice,
  badgeText,
  label = DEFAULT_CURRENCY_BAR_LABEL,
  mobileLabel,
  description,
  tooltip,
  location,
  className,
  onCurrencyChange,
  multiCurrencyConfig,
  compactDismiss = false,
  countryAutoDetection,
  wideLabelGap = false,
}: CurrencyBarProps) {
  const t = useTranslations("Currency");
  const [isDismissed, setIsDismissed] = useState(false);
  const isMobile = useIsMobile(description ? 1024 : 768);
  const { setCurrency } = useCurrency();

  const { elementClicked: dismissClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: MINI_BANNER_DISMISS_HTML_ID,
      location,
      name: MINI_BANNER_DISMISS_NAME,
      text: MINI_BANNER_DISMISS_TEXT,
    },
  });

  const handleDismiss = useCallback(() => {
    dismissClicked();
    setCurrency(DEFAULT_CURRENCY);
    setIsDismissed(true);
  }, [dismissClicked, setCurrency]);

  if (isDismissed) {
    return null;
  }

  const dismissButton = (
    <button
      type="button"
      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      aria-label={t("dismissBar")}
      data-testid="currency-bar-dismiss"
      onClick={handleDismiss}
    >
      <Icon
        type="close"
        svgProps={{
          width: 20,
          height: 20,
          "aria-hidden": true,
        }}
      />
    </button>
  );

  const resolvedLabel =
    label === DEFAULT_CURRENCY_BAR_LABEL ? t("earnCertificateUnlock") : label;
  const resolvedMobileLabel =
    mobileLabel === DEFAULT_CURRENCY_BAR_MOBILE_LABEL
      ? t("chooseLocalCurrency")
      : mobileLabel;
  const mobileHeaderLabel = resolvedMobileLabel ?? resolvedLabel;

  const badge = badgeText ? (
    <BadgeIcon
      icon="none"
      color="primaryLight"
      withBorder
      iconSize={14}
      className="shrink-0 whitespace-nowrap gap-2 md:border-primary-light md:bg-secondary-500 md:text-white"
    >
      <span className="text-micro">{badgeText}</span>
    </BadgeIcon>
  ) : null;

  return (
    <div
      className={cn("w-full bg-[var(--Brand-Elm-200,#BFC9CA)] py-3", className)}
      data-testid="currency-bar"
      role="region"
      aria-label={t("selectionBarAriaLabel")}
    >
      <div
        className={cn(
          "mx-auto flex w-full max-w-screen-xl px-4",
          description && !isMobile ? "justify-between gap-3" : "gap-3",
        )}
      >
        {isMobile ? (
          <div className="flex w-full flex-col gap-2">
            {description && (
              <div className="relative">
                <div className={cn(CURRENCY_BAR_LABEL_CLASS_NAME, "pr-8")}>
                  {description}
                </div>
                <div className="absolute -right-3 top-1/2 -translate-y-1/2">
                  {dismissButton}
                </div>
              </div>
            )}
            <div className={cn(!description && "relative")}>
              <div
                className={cn(
                  "flex flex-wrap items-center gap-2",
                  !description && "pr-8",
                )}
              >
                {badge}
                <p className={cn(CURRENCY_BAR_LABEL_CLASS_NAME, "m-0")}>
                  {mobileHeaderLabel}
                </p>
              </div>
              {!description && (
                <div className="absolute -right-3 top-1/2 -translate-y-1/2">
                  {dismissButton}
                </div>
              )}
            </div>
            <CurrencyInlinePill
              price={price}
              originalPrice={originalPrice}
              tooltip={tooltip}
              location={location}
              layout="bar"
              stacked
              onCurrencyChange={onCurrencyChange}
              multiCurrencyConfig={multiCurrencyConfig}
              countryAutoDetection={countryAutoDetection}
            />
          </div>
        ) : (
          <>
            {description && (
              <div
                className={cn(CURRENCY_BAR_LABEL_CLASS_NAME, "max-w-[533px]")}
              >
                {description}
              </div>
            )}
            <div
              className={cn(
                description
                  ? "flex items-center gap-3"
                  : cn(
                      "min-w-0 self-center flex items-center gap-2",
                      !compactDismiss && "flex-1",
                    ),
              )}
            >
              {badge}
              <CurrencyInlinePill
                price={price}
                originalPrice={originalPrice}
                label={resolvedLabel}
                tooltip={tooltip}
                location={location}
                layout="bar"
                wideLabelGap={wideLabelGap}
                onCurrencyChange={onCurrencyChange}
                multiCurrencyConfig={multiCurrencyConfig}
                countryAutoDetection={countryAutoDetection}
              />
              {description && dismissButton}
            </div>
            {!description && dismissButton}
          </>
        )}
      </div>
    </div>
  );
}
